import React from 'react';
import { Link } from 'react-router-dom';
import { PageTitle } from '../components/PageTitle';
import { Helmet } from 'react-helmet-async';
import { ProfessionalJourney } from '../components/ProfessionalJourney';
import { BriefcaseIcon, BarChart2Icon, PaletteIcon, UsersIcon, TrendingUpIcon, CompassIcon, BrainIcon, GlobeIcon, HeartIcon, LightbulbIcon } from 'lucide-react';
export function About() {
  return <div className="bg-white" data-id="element-202">
      <PageTitle title="About Me" canonical="/about" description="Learn about Katalina Bock's background as a product and marketing leader with experience spanning Fortune 50, unicorn, and startup companies." keywords="about Katalina Bock, product leader, marketing leader, professional background, Harvard Business School, Duke University, Verkada, Productboard, Amazon, Johnson & Johnson" data-id="element-203" />
      <Helmet data-id="element-204">
        <script type="application/ld+json" data-id="element-205">
          {JSON.stringify({
          '@context': 'https://schema.org',
          '@type': 'Person',
          name: 'Katalina Bock',
          url: 'https://katalinabock.com',
          image: 'https://uploadthingy.s3.us-west-1.amazonaws.com/93EkAeB1832rjEHAhKY7UP/Untitled_design_%281%29.png',
          jobTitle: 'Product Leader',
          worksFor: {
            '@type': 'Organization',
            name: 'Verkada'
          },
          alumniOf: [{
            '@type': 'CollegeOrUniversity',
            name: 'Harvard Business School'
          }, {
            '@type': 'CollegeOrUniversity',
            name: 'Duke University'
          }],
          knowsAbout: ['Product Management', 'Product Strategy', 'Growth', 'Innovation', 'Cross-Functional Leadership'],
          description: 'Product leader specializing in product strategy, growth, and innovation with impact across Fortune 50, unicorn, and startup companies.'
        })}
        </script>
      </Helmet>
      {/* Hero Section */}
      <section className="bg-gradient-to-r from-[#E0F5F8] to-[#F0FAFC] text-gray-900 py-20" data-id="element-206">
        <div className="container mx-auto px-4" data-id="element-207">
          <div className="flex flex-col md:flex-row items-center" data-id="element-208">
            <div className="md:w-1/3 mb-8 md:mb-0 flex justify-center" data-id="element-209">
              <div className="w-64 h-64 rounded-full overflow-hidden border-4 border-[#7DCCD8]" data-id="element-210">
                <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/93EkAeB1832rjEHAhKY7UP/Untitled_design_%281%29.png" alt="Katalina Bock headshot" className="w-full h-full object-cover grayscale" data-id="element-211" />
              </div>
            </div>
            <div className="md:w-2/3 md:pl-12" data-id="element-212">
              <h1 className="text-4xl font-bold mb-4" data-id="element-213">Katalina Bock</h1>
              <p className="text-xl mb-6 text-gray-700" data-id="element-214">
                Delivering growth, innovation, and market expansion across
                consumer-centric tech companies.
              </p>
              <p className="mb-6 text-gray-600" data-id="element-215">
                With experience spanning product management, product growth, and
                product marketing, I thrive at the intersection of product
                strategy, customer psychology, and market insights. I'm known
                for blending analytical rigor with creative storytelling and
                influencing executives to align on bold growth bets.
              </p>
              <div className="flex gap-4" data-id="element-216">
                <a href="https://www.linkedin.com/in/katalinabock" target="_blank" rel="noopener noreferrer" className="bg-[#0096B4] hover:bg-[#00778F] text-white px-6 py-3 rounded-md font-medium transition-colors duration-200 inline-flex items-center" data-id="element-217">
                  LinkedIn Profile
                </a>
                <Link to="/contact" className="bg-transparent border border-gray-700 hover:bg-gray-700 hover:text-white text-gray-700 px-6 py-3 rounded-md font-medium transition-colors duration-200" data-id="element-218">
                  Contact Me
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* My Journey Section - Replaced with new component */}
      <ProfessionalJourney data-id="element-219" />

      {/* Education Section */}
      <section className="py-10 bg-[#E0F5F8]" data-id="element-220">
        <div className="container mx-auto px-4" data-id="element-221">
          <h2 className="text-3xl font-bold text-gray-900 mb-8 text-center" data-id="element-222">
            Education
          </h2>
          <div className="max-w-3xl mx-auto" data-id="element-223">
            {/* Harvard */}
            <div className="mb-6 bg-white p-4 rounded-lg shadow-md" data-id="element-224">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-2" data-id="element-225">
                <div className="flex items-center" data-id="element-226">
                  <div className="bg-[#E0F5F8] p-2 rounded-full mr-3 w-12 h-12 flex items-center justify-center" data-id="element-227">
                    <span className="text-[#0096B4] font-bold" data-id="element-228">MBA</span>
                  </div>
                  <h3 className="text-xl font-bold" data-id="element-229">Harvard Business School</h3>
                </div>
                <div className="mt-1 md:mt-0" data-id="element-230">
                  <span className="text-[#0096B4] font-medium" data-id="element-231">Boston, MA</span>
                </div>
              </div>
              <div className="ml-12" data-id="element-232">
                <p className="text-gray-800" data-id="element-233">
                  <span className="font-medium" data-id="element-234">
                    Master of Business Administration
                  </span>{' '}
                  (Management Science Focus)
                </p>
              </div>
            </div>
            {/* Duke */}
            <div className="bg-white p-4 rounded-lg shadow-md" data-id="element-235">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-2" data-id="element-236">
                <div className="flex items-center" data-id="element-237">
                  <div className="bg-[#E0F5F8] p-2 rounded-full mr-3 w-12 h-12 flex items-center justify-center" data-id="element-238">
                    <span className="text-[#0096B4] font-bold" data-id="element-239">BA</span>
                  </div>
                  <h3 className="text-xl font-bold" data-id="element-240">Duke University</h3>
                </div>
                <div className="mt-1 md:mt-0" data-id="element-241">
                  <span className="text-[#0096B4] font-medium" data-id="element-242">Durham, NC</span>
                </div>
              </div>
              <div className="ml-12" data-id="element-243">
                <p className="text-gray-800 font-medium" data-id="element-244">
                  B.A. in Psychology, Markets and Management, Dance Minor
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* What I Bring Section */}
      <section className="py-10 bg-white" data-id="element-245">
        <div className="container mx-auto px-4" data-id="element-246">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center" data-id="element-247">
            What I Bring to the Table
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 max-w-4xl mx-auto" data-id="element-248">
            <div className="bg-[#E0F5F8] p-4 rounded-lg shadow-md text-center" data-id="element-249">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-[#7DCCD8] text-[#0096B4] rounded-full mb-3" data-id="element-250">
                <CompassIcon size={28} data-id="element-251" />
              </div>
              <h3 className="text-xl font-semibold mb-2" data-id="element-252">Strategic Vision</h3>
              <p className="text-gray-600" data-id="element-253">
                I identify market opportunities and translate customer insights
                into compelling product strategies by fusing psychology, data,
                and market analysis to uncover the "why" behind behavior.
              </p>
            </div>
            <div className="bg-[#E0F5F8] p-4 rounded-lg shadow-md text-center" data-id="element-254">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-[#7DCCD8] text-[#0096B4] rounded-full mb-3" data-id="element-255">
                <UsersIcon size={28} data-id="element-256" />
              </div>
              <h3 className="text-xl font-semibold mb-2" data-id="element-257">
                Cross-Functional Leadership
              </h3>
              <p className="text-gray-600" data-id="element-258">
                I unite engineers, designers, marketers, and executives around a
                shared vision, aligning diverse perspectives to move complex
                products from idea to impact.
              </p>
            </div>
            <div className="bg-[#E0F5F8] p-4 rounded-lg shadow-md text-center" data-id="element-259">
              <div className="inline-flex items-center justify-center w-14 h-14 bg-[#7DCCD8] text-[#0096B4] rounded-full mb-3" data-id="element-260">
                <LightbulbIcon size={28} data-id="element-261" />
              </div>
              <h3 className="text-xl font-semibold mb-2" data-id="element-262">
                Innovative Problem-Solving
              </h3>
              <p className="text-gray-600" data-id="element-263">
                I pair analytical rigor with creativity to generate
                out-of-the-box solutions that are both bold in vision and
                practical in delivery.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* Technical Tools Section */}
      <section className="py-10 bg-[#E0F5F8]" data-id="element-264">
        <div className="container mx-auto px-4" data-id="element-265">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center" data-id="element-266">
            Technical Tools
          </h2>
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md p-5" data-id="element-267">
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-id="element-268">
              <div data-id="element-269">
                <div className="flex items-center mb-2" data-id="element-270">
                  <div className="bg-[#0096B4] p-2 rounded-full mr-2" data-id="element-271">
                    <BriefcaseIcon size={16} className="text-white" data-id="element-272" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-273">
                    Product & Project
                  </h3>
                </div>
                <div className="flex flex-wrap gap-1 ml-8" data-id="element-274">
                  {['JIRA', 'Productboard', 'Monday', 'Notion', 'Asana', 'Basecamp', 'Miro'].map((tool, index) => <span key={index} className="bg-[#E0F5F8] text-[#0096B4] px-2 py-1 rounded-full text-xs font-medium mb-1" data-id="element-275">
                      {tool}
                    </span>)}
                </div>
              </div>
              <div data-id="element-276">
                <div className="flex items-center mb-2" data-id="element-277">
                  <div className="bg-[#0096B4] p-2 rounded-full mr-2" data-id="element-278">
                    <BarChart2Icon size={16} className="text-white" data-id="element-279" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-280">
                    Data & Analytics
                  </h3>
                </div>
                <div className="flex flex-wrap gap-1 ml-8" data-id="element-281">
                  {['SQL', 'Tableau', 'Power BI', 'Google Analytics', 'Excel', 'Looker'].map((tool, index) => <span key={index} className="bg-[#E0F5F8] text-[#0096B4] px-2 py-1 rounded-full text-xs font-medium mb-1" data-id="element-282">
                      {tool}
                    </span>)}
                </div>
              </div>
              <div data-id="element-283">
                <div className="flex items-center mb-2" data-id="element-284">
                  <div className="bg-[#0096B4] p-2 rounded-full mr-2" data-id="element-285">
                    <PaletteIcon size={16} className="text-white" data-id="element-286" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-287">
                    Design & Creative
                  </h3>
                </div>
                <div className="flex flex-wrap gap-1 ml-8" data-id="element-288">
                  {['Adobe Creative Cloud', 'Adobe Express', 'Lightroom', 'Canva', 'Magic Patterns', 'Framer', 'WordPress', 'Pinterest'].map((tool, index) => <span key={index} className="bg-[#E0F5F8] text-[#0096B4] px-2 py-1 rounded-full text-xs font-medium mb-1" data-id="element-289">
                      {tool}
                    </span>)}
                </div>
              </div>
              <div data-id="element-290">
                <div className="flex items-center mb-2" data-id="element-291">
                  <div className="bg-[#0096B4] p-2 rounded-full mr-2" data-id="element-292">
                    <TrendingUpIcon size={16} className="text-white" data-id="element-293" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-294">
                    CRM & Marketing
                  </h3>
                </div>
                <div className="flex flex-wrap gap-1 ml-8" data-id="element-295">
                  {['Salesforce', 'Marketo', 'Mailchimp', 'ActiveCampaign', 'SEO'].map((tool, index) => <span key={index} className="bg-[#E0F5F8] text-[#0096B4] px-2 py-1 rounded-full text-xs font-medium mb-1" data-id="element-296">
                      {tool}
                    </span>)}
                </div>
              </div>
              <div data-id="element-297">
                <div className="flex items-center mb-2" data-id="element-298">
                  <div className="bg-[#0096B4] p-2 rounded-full mr-2" data-id="element-299">
                    <BrainIcon size={16} className="text-white" data-id="element-300" />
                  </div>
                  <h3 className="text-lg font-semibold text-gray-900" data-id="element-301">
                    AI & Automation
                  </h3>
                </div>
                <div className="flex flex-wrap gap-1 ml-8" data-id="element-302">
                  {['Magic Patterns', 'Claude', 'ChatGPT', 'Gemini', 'Perplexity', 'Cursor', 'Xcode'].map((tool, index) => <span key={index} className="bg-[#E0F5F8] text-[#0096B4] px-2 py-1 rounded-full text-xs font-medium mb-1" data-id="element-303">
                      {tool}
                    </span>)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Personal Section */}
      <section className="py-10 bg-white" data-id="element-304">
        <div className="container mx-auto px-4" data-id="element-305">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center" data-id="element-306">
            Beyond the Boardroom
          </h2>
          <div className="max-w-4xl mx-auto" data-id="element-307">
            {/* Cultural Background */}
            <div className="mb-6 bg-white p-4 rounded-lg shadow-md" data-id="element-308">
              <div className="flex items-center mb-3" data-id="element-309">
                <div className="bg-[#E0F5F8] p-2 rounded-full mr-3" data-id="element-310">
                  <GlobeIcon size={24} className="text-[#0096B4]" data-id="element-311" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900" data-id="element-312">
                  Global Citizen
                </h3>
              </div>
              <div className="pl-12" data-id="element-313">
                <p className="text-gray-700 mb-2" data-id="element-314">
                  As a dual citizen who has lived in Germany, Switzerland, and
                  the United States, I bring a truly global perspective to my
                  work and life.
                </p>
                <div className="flex flex-wrap gap-2 mb-2" data-id="element-315">
                  <span className="bg-[#0096B4] text-white px-3 py-1 rounded-full text-sm" data-id="element-316">
                    Native German
                  </span>
                  <span className="bg-[#0096B4] text-white px-3 py-1 rounded-full text-sm" data-id="element-317">
                    Fluent English
                  </span>
                  <span className="bg-[#7DCCD8] text-white px-3 py-1 rounded-full text-sm" data-id="element-318">
                    Conversational French
                  </span>
                  <span className="bg-[#7DCCD8] text-white px-3 py-1 rounded-full text-sm" data-id="element-319">
                    Conversational Spanish
                  </span>
                </div>
              </div>
            </div>
            {/* Active Engagements */}
            <div className="mb-6 bg-white p-4 rounded-lg shadow-md" data-id="element-320">
              <div className="flex items-center mb-3" data-id="element-321">
                <div className="bg-[#E0F5F8] p-2 rounded-full mr-3" data-id="element-322">
                  <HeartIcon size={24} className="text-[#0096B4]" data-id="element-323" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900" data-id="element-324">
                  Active Engagements
                </h3>
              </div>
              <div className="pl-12" data-id="element-325">
                <p className="text-gray-700 mb-2" data-id="element-326">
                  I'm passionate about giving back through mentorship and
                  community involvement:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2" data-id="element-327">
                  <div className="bg-[#F0FAFC] p-3 rounded-lg" data-id="element-328">
                    <h4 className="font-medium text-[#0096B4] mb-1" data-id="element-329">
                      Industry Mentor at HBS
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-330">
                      Guiding students at Harvard Business School in their
                      product management career paths.
                    </p>
                  </div>
                  <div className="bg-[#F0FAFC] p-3 rounded-lg" data-id="element-331">
                    <h4 className="font-medium text-[#0096B4] mb-1" data-id="element-332">
                      Admissions Interviewer at Duke
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-333">
                      Helping select the next generation of Duke University
                      students.
                    </p>
                  </div>
                  <div className="bg-[#F0FAFC] p-3 rounded-lg" data-id="element-334">
                    <h4 className="font-medium text-[#0096B4] mb-1" data-id="element-335">
                      Mentor at Twenty
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-336">
                      Supporting emerging professionals through the Twenty
                      platform.
                    </p>
                  </div>
                  <div className="bg-[#F0FAFC] p-3 rounded-lg" data-id="element-337">
                    <h4 className="font-medium text-[#0096B4] mb-1" data-id="element-338">
                      Women's Association Mentor at HBS
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-339">
                      Empowering women in business through dedicated mentorship.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            {/* Creative Pursuits */}
            <div className="bg-white p-4 rounded-lg shadow-md" data-id="element-340">
              <div className="flex items-center mb-3" data-id="element-341">
                <div className="bg-[#E0F5F8] p-2 rounded-full mr-3" data-id="element-342">
                  <PaletteIcon size={24} className="text-[#0096B4]" data-id="element-343" />
                </div>
                <h3 className="text-xl font-semibold text-gray-900" data-id="element-344">
                  Creative Expression
                </h3>
              </div>
              <div className="pl-12" data-id="element-345">
                <p className="text-gray-700 mb-2" data-id="element-346">
                  My creative spirit finds expression through multiple artistic
                  outlets:
                </p>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-2" data-id="element-347">
                  <div className="bg-[#F0FAFC] p-3 rounded-lg" data-id="element-348">
                    <h4 className="font-medium text-[#0096B4] mb-1" data-id="element-349">
                      Life-long Dancer
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-350">
                      Performed at the London Olympics and continue to dance as
                      a form of expression and joy.
                    </p>
                  </div>
                  <div className="bg-[#F0FAFC] p-3 rounded-lg" data-id="element-351">
                    <h4 className="font-medium text-[#0096B4] mb-1" data-id="element-352">
                      Visual Artist
                    </h4>
                    <p className="text-gray-600 text-sm" data-id="element-353">
                      Graphic designer and acrylic/mixed media painter with a
                      passion for visual storytelling.
                    </p>
                  </div>
                  <div className="bg-[#F0FAFC] p-3 rounded-lg" data-id="element-354">
                    <h4 className="font-medium text-[#0096B4] mb-1" data-id="element-355">Writer</h4>
                    <p className="text-gray-600 text-sm" data-id="element-356">
                      Poetry writer who finds meaning and beauty in the power of
                      words and language.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Certifications & Programs Section */}
      <section className="py-10 bg-[#E0F5F8]" data-id="element-357">
        <div className="container mx-auto px-4" data-id="element-358">
          <h2 className="text-3xl font-bold text-gray-900 mb-6 text-center" data-id="element-359">
            Certifications & Programs
          </h2>
          <div className="max-w-4xl mx-auto bg-white rounded-xl shadow-md p-5" data-id="element-360">
            <div className="columns-1 md:columns-3 gap-4" data-id="element-361">
              {[{
              name: 'Generative AI',
              provider: 'Microsoft'
            }, {
              name: 'Machine Learning',
              provider: 'Amazon'
            }, {
              name: 'Agile',
              provider: 'LinkedIn'
            }, {
              name: 'Data Analytics',
              provider: 'DataCamp'
            }, {
              name: 'Vibe Coding',
              provider: 'LinkedIn'
            }, {
              name: 'Product Management',
              provider: 'Aha!'
            }, {
              name: 'Technical Product Management',
              provider: 'LinkedIn'
            }, {
              name: 'Advanced SQL',
              provider: 'Afterskills'
            }, {
              name: 'CM1 Certified Marketer',
              provider: 'General Assembly'
            }, {
              name: 'YouTube Content Strategy',
              provider: 'YouTube'
            }, {
              name: 'Facebook Blueprint',
              provider: 'Facebook'
            }].map((cert, index) => <div key={index} className="mb-3 break-inside-avoid" data-id="element-362">
                  <div className="flex items-center" data-id="element-363">
                    <div className="w-2 h-2 rounded-full bg-[#0096B4] mr-2" data-id="element-364"></div>
                    <div data-id="element-365">
                      <h3 className="font-medium text-gray-800 text-sm" data-id="element-366">
                        {cert.name}
                      </h3>
                      <p className="text-gray-500 text-xs" data-id="element-367">{cert.provider}</p>
                    </div>
                  </div>
                </div>)}
            </div>
          </div>
        </div>
      </section>
    </div>;
}